import { useIntl } from 'react-intl';
import { createColumnHelper } from '@tanstack/react-table';

import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import useFilters from 'src/registry/hooks/useFilters';
import { booleanBadgeOptions } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { CURRENCY_BADGE_OPTIONS } from 'src/features/moneyAccounts/utils/constants';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { useListReferenceRates } from 'src/features/referenceRates/api/useListReferenceRates';
import { referenceRatesMessages } from 'src/features/referenceRates/utils/messages';
import { ReferenceRate } from 'src/features/referenceRates/utils/types';
import CellReferenceRateActions from 'src/features/referenceRates/components/tables/CellReferenceRateActions';
import CreateReferenceRateFormModal from 'src/features/referenceRates/components/forms/CreateReferenceRateFormModal';

const ReferenceRatesTable = () => {
    const { formatMessage, formatNumber } = useIntl();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { querySearchOptions, ...search } = useSearch();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });

    const {
        data: referenceRates,
        isLoading,
        isError,
    } = useListReferenceRates({
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
    });

    const columnHelper = createColumnHelper<ReferenceRate>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: (ctx) => <CellLinkWithIcon {...ctx} to={`/reference-rates/${ctx.row.original.id}`} />,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('code', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(referenceRatesMessages.code),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.accessor('currency', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: ({ row }) => <CellCurrencyBadge currency={row.original.currency} />,
            meta: {
                title: formatMessage(commonMessages.currency),
                badgeOptions: CURRENCY_BADGE_OPTIONS,
            },
        }),
        // `amount` is in minor units and `current_value` is null when nothing covers today - which
        // is exactly the state that makes a policy indexed to this rate fail to save.
        columnHelper.display({
            id: 'current_value',
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const currentValue = row.original.current_value;

                if (!currentValue) {
                    return (
                        <span className="whitespace-nowrap text-gray-500">
                            {formatMessage(referenceRatesMessages.noCurrentValue)}
                        </span>
                    );
                }

                return (
                    <span className="whitespace-nowrap">
                        {formatNumber(currentValue.amount / 100, {
                            style: 'currency',
                            currency: row.original.currency,
                        })}
                    </span>
                );
            },
            meta: {
                title: formatMessage(referenceRatesMessages.currentValue),
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(referenceRatesMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        // Always rendered - the cell gates edit/delete on its own so that read-only users still
        // get the dashboard shortcut.
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellReferenceRateActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<ReferenceRate, unknown>
            columns={columns}
            data={referenceRates}
            semantics={{
                title: formatMessage(referenceRatesMessages.referenceRates),
                entryName: formatMessage(referenceRatesMessages.rate).toLowerCase(),
                entryNames: formatMessage(referenceRatesMessages.rates).toLowerCase(),
                placeholder: formatMessage(referenceRatesMessages.rate).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlans && { newEntry: CreateReferenceRateFormModal }),
            }}
        />
    );
};

export default ReferenceRatesTable;
